import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { type FC, useState } from "react";
import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { config } from "../../../../config";
import type { SlackConfig } from "../../../../pages/manage/manage-channels-page";
import { useApiServiceCallback } from "../../../../server/api/client";
import { useSupabase, useSupabaseCallback } from "../../../../server/supabase/hooks";
import { SlackIcon } from "../../../../styles/icons/slack";
import { LocalStorageService } from "../../../../utils/local-storage";
import { getMediaEngineDomain } from "../../../../utils/media-engine-domain";
import { type UUID, uuidv4 } from "../../../../utils/uuid";
import {
  type HasIntegration,
  IntegrationCard,
} from "../../../generic/components/cards/integration-card";
import { SlackService } from "../client";
import { type AppVersion } from "../types/slack-app-version";

const storageKey = "slackState";

export const SlackCard: FC<{
  setSlackConfigs: (slackConfigs: SlackConfig[]) => void;
  slackConfigId?: UUID;
  slackConfigName?: string | null;
  slackAppId?: UUID | null;
  needsUpdate?: boolean;
}> = ({ slackConfigId, setSlackConfigs, slackConfigName, slackAppId, needsUpdate = false }) => {
  const [hasIntegration, setHasIntegration] = useState<HasIntegration>(!!slackConfigId);

  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [warning, setWarning] = useState(false);

  const setSlackInfo = useApiServiceCallback(
    SlackService,
    async ({ apiService }, state: string, code: string) => {
      const { error } = await apiService.setSlackInfo(code);

      if (error) {
        toast.error(t("Something went wrong"));
        return;
      }
      const localStorage = new LocalStorageService();
      localStorage.remove("slackState");
      toast.success(t("Slack successfully connected"));
    },
    [],
  );

  const updateSlackConfigs = useSupabaseCallback(
    async ({ supabase }) => {
      const { data } = await supabase
        .from("slack_config")
        .select("id,name,slack_app_id,slack_app(version)");

      if (!data) return;

      setSlackConfigs(data);
    },
    [setSlackConfigs],
  );

  useSupabase(async () => {
    if (hasIntegration !== false) return;

    const state = searchParams.get("state");
    const code = searchParams.get("code");

    if (!state || !code) return;

    const localStorage = new LocalStorageService();
    setHasIntegration("loading");

    if (localStorage.get("slackState") === state) {
      await setSlackInfo(state, code);
      setSearchParams({});
    } else {
      toast.error(t("Something went wrong"));
      return;
    }

    await updateSlackConfigs();
    setHasIntegration(false);
  }, [hasIntegration, searchParams, updateSlackConfigs, setSlackInfo, setSearchParams, t]);

  const disableIntegration = useApiServiceCallback(
    SlackService,
    async ({ apiService }) => {
      if (!slackConfigId) return;

      setWarning(false);
      setHasIntegration("loading");

      const { error } = await apiService.disconnect(slackConfigId);
      if (!error) {
        toast.success(t("Slack successfully disconnected"));
        setHasIntegration(false);
        await updateSlackConfigs();
      } else {
        toast.error(t("There was an error disconnecting Slack, please contact support."));
      }
    },
    [slackConfigId],
  );

  return (
    <Box sx={{ maxWidth: "500px" }}>
      <IntegrationCard
        title={`Slack${slackConfigName ? " - " + slackConfigName : ""}`}
        subtitle={t(
          "Integrate your Communications with Slack. Send direct messages to your employees via our ChangeEngine Chatbot.",
        )}
        hasIntegration={hasIntegration}
        disableIntegration={() => Promise.resolve(setWarning(true))}
        connectButton={<ConnectButton appVersion="4" />}
        icon={<SlackIcon />}
        extraAction={
          needsUpdate && slackAppId ? <UpdateButton slackAppId={slackAppId} /> : undefined
        }
      />
      <Dialog open={warning}>
        <DialogTitle>
          <Alert severity="warning">
            <Trans>Communications will be lost</Trans>
          </Alert>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Trans>
              If you remove the ChangeEngine Bot from Slack then your employees will no longer be
              able to access old Communications or receive future Communications.
            </Trans>
          </DialogContentText>
          <DialogActions>
            <Button
              data-analytics-id="slack-configuration-card-warning-dialog-cancel"
              onClick={() => setWarning(false)}
            >
              <Trans>Continue using Slack</Trans>
            </Button>
            <Button
              data-analytics-id="slack-config-card-warning-dialog-confirm"
              color="warning"
              onClick={() => {
                void disableIntegration().catch(console.error);
              }}
            >
              <Trans>Remove old Communications from Slack</Trans>
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const ConnectButton: FC<{ appVersion?: AppVersion }> = ({ appVersion }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const onClick = useApiServiceCallback(
    SlackService,
    async ({ apiService }) => {
      setLoading(true);

      const state = uuidv4();
      const { data, error } = await apiService.build_app(
        state,
        appVersion ?? "2",
        appVersion === "4" ? getMediaEngineDomain(state) : undefined,
      );
      if (!data || error) {
        toast.error("Something went wrong");
        setLoading(false);
        return;
      }

      const localStorage = new LocalStorageService();

      const params = new URLSearchParams({
        scope:
          appVersion === "4"
            ? config.slack.bot_scopes_v4.join(",")
            : config.slack.bot_scopes_v2.join(","),
        user_scope: config.slack.user_scopes_v2.join(","),
        redirect_uri: `${window.location.origin}/channels/slack`,
        client_id: data.clientId,
        state,
      });
      const url = `https://slack.com/oauth/v2/authorize?${params.toString()}`;
      localStorage.set(storageKey, state);
      window.location.href = url;
      setLoading(false);
    },
    [],
  );

  return (
    <Button
      data-analytics-id="slack-configuration-card-connect"
      startIcon={<SlackIcon />}
      variant="outlined"
      color={"success"}
      onClick={() => void onClick().catch(console.error)}
      loading={loading}
    >
      {t("Connect")}
    </Button>
  );
};

const UpdateButton: FC<{ slackAppId: UUID }> = ({ slackAppId }) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const onClick = useApiServiceCallback(
    SlackService,
    async ({ apiService }) => {
      setLoading(true);

      const { data, error } = await apiService.update_app(
        slackAppId,
        getMediaEngineDomain(slackAppId),
      );
      if (!data || error) {
        toast.error("Something went wrong");
        setLoading(false);
        return;
      }

      const localStorage = new LocalStorageService();

      const state = uuidv4();
      const params = new URLSearchParams({
        scope: config.slack.bot_scopes_v4.join(","),
        user_scope: config.slack.user_scopes_v2.join(","),
        redirect_uri: `${window.location.origin}/channels/slack`,
        client_id: data.clientId,
        state,
      });
      const url = `https://slack.com/oauth/v2/authorize?${params.toString()}`;
      localStorage.set(storageKey, state);
      window.location.href = url;
      setLoading(false);
    },
    [],
  );

  return (
    <Tooltip title="Update your Slack Integration to enable embedded video">
      <Button
        startIcon={<SlackIcon />}
        variant="outlined"
        color={"success"}
        onClick={() => void onClick()}
        loading={loading}
        data-analytics-id="slack-config-card-update"
      >
        {t("Update")}
      </Button>
    </Tooltip>
  );
};
